import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import useForm from '../../lib/hooks/useForm';
import { saveTile } from './data';
import useGetAdmins from './hooks/useGetAdmins';
import { defaultAnswers, questionDefaultValue } from './utils/helpers';
import { QUIZ, AUTOSAVE_TIMER } from './utils/constants';
import { tileSchema } from './utils/tileSchema';
import { useNotification } from '../Notification/useNotification';

const boardData = JSON.parse(document.body.dataset.currentBoard);

const emptyTileData = (postId, position) => {
  return {
    headline: '',
    attachments: {},
    showDisclaimerText: false,
    disclaimerText: '',
    supportingContent: '',
    description: '',
    isAnonymous: false,
    points: 10,
    dependentBoardEnabled: boardData.dependent_board_enabled,
    questionType: 'action',
    questionSubtype: 'read_tile',
    answers: ['I read it'],
    isPublic: false,
    isSharable: false,
    demoId: boardData.id,
    audienceId: '',
    ribbonTagId: '',
    campaignContainer: '',
    postId,
    newPosition: Number(position),
  };
};

export const useTileForm = ({ tile, onSave, postId, position }) => {
  const tileData = tile || emptyTileData(postId, position);
  const admins = useGetAdmins(tileData.demoId);
  const [autoSaved, setAutoSaved] = useState({
    time: '',
    status: '',
  });
  const [autoSaveFailed, setAutoSaveFailed] = useState(false);
  const [initialValues, setInitialValues] = useState({
    answers: [],
    ...tileData,
    remoteMediaUrl: tileData.remoteMediaUrl,
    question:
      tileData.question ||
      questionDefaultValue(tileData.questionType, tileData.questionSubtype),
    customQuestion: !!tileData.question,
  });
  const { showError } = useNotification();
  const { t } = useTranslation('tileForm');

  const { mutate: saveTileMutation } = useMutation(saveTile, {
    onSuccess: ({ data }) => {
      onSave({ ...data });
    },
    onError: err => {
      if (err.response.data.message) return;

      showError({
        title: t('error.saveErrorTitle'),
        content: t('error.saveErrorContent'),
        disappear: false,
      });
    },
  });

  const form = useForm({
    initialValues,
    enableReinitialize: true,
    validationSchema: tileSchema,
    ribbonTagId: tileData.ribbonTagId || '',
    audienceId: tileData.audienceId || '',
  });

  const saveAndNavigate = navigateFunction => {
    if (form.dirty) {
      saveTileMutation(form.values, {
        onSuccess: () => {
          navigateFunction();
        },
      });
    } else {
      navigateFunction();
    }
  };

  const { mutate: autoSave } = useMutation(saveTile, {
    onSuccess: ({ data }) => {
      const { tileId, remoteMediaUrl } = data;

      if (!form.values.remoteMediaUrl && remoteMediaUrl) {
        form.setFieldValue('remoteMediaUrl', remoteMediaUrl);
      }

      if (!form.values.id && tileId) {
        form.setFieldValue('id', tileId);
      }

      onSave({ ...data });
      setAutoSaveFailed(false);
      setAutoSaved({
        time: format(new Date(), 'h:mm aaa'),
        status: 'saved',
      });

      form.validateForm();
    },
    // A failed autosave re-renders this hook, which re-runs the effect below and
    // re-arms the timer — so without this flag the form retries every
    // AUTOSAVE_TIMER ms indefinitely. The global MutationCache in
    // lib/queryClient.js already surfaces the API's error message.
    onError: () => {
      setAutoSaveFailed(true);
      setAutoSaved({ time: '', status: 'error' });
    },
  });

  useEffect(() => {
    const isEditingTile = !!form.values.id;

    if (isEditingTile) {
      form.validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.id]);

  // Give a failed autosave another chance as soon as the admin edits again, so a
  // transient failure doesn't disable autosave for the rest of the session.
  useEffect(() => {
    setAutoSaveFailed(false);
  }, [form.values]);

  useEffect(() => {
    if (!form.dirty || autoSaveFailed) return undefined;
    const timer = setTimeout(() => {
      setAutoSaved({ time: '', status: 'saving' });
      autoSave(form.values);
    }, AUTOSAVE_TIMER);
    return () => clearTimeout(timer);
  }, [autoSave, form, form.dirty, autoSaveFailed]);

  const addAttachment = (fileName, location) => {
    form.setFieldValue('attachments', {
      ...form.values.attachments,
      [fileName]: location,
    });
  };

  const removeAttachment = e => {
    const attachmentsCopy = { ...form.values.attachments };
    delete attachmentsCopy[e.target.attributes.name.value];
    form.setFieldValue('attachments', attachmentsCopy);
  };

  const handleShowDisclaimer = e => {
    const showDisclaimer = e.target.checked;
    form.setFieldValue('showDisclaimerText', showDisclaimer);

    if (!showDisclaimer) {
      form.setFieldValue('disclaimerText', '');
    }
  };

  const handleQuestionOnChange = event => {
    form.setValues({
      ...form.values,
      question: event.target.value,
      customQuestion: true,
    });
  };

  const handleQuestionSubTypeOnChange = (type, option) => {
    form.setValues({
      ...form.values,
      questionType: type,
      questionSubtype: option,
      answers: defaultAnswers(type, option),
      isAnonymous: false,
      allowFreeResponse: false,
      correctAnswerIndex: -1,
      assigneeId: null,
    });

    if (type === QUIZ) {
      form.validateField('correctAnswerIndex');
      form.validateField('answers');
    }

    if (!form.values.customQuestion) {
      form.setFieldValue('question', questionDefaultValue(type, option));
    }
  };

  const handleAddAnswer = answer => {
    form.setFieldValue('answers', [...form.values.answers, answer]);
  };

  const handleRemoveAnswer = index => {
    const answers = [...form.values.answers];
    answers.splice(index, 1);
    form.setFieldValue('answers', answers);
  };

  const removeMedia = () => {
    form.setValues({
      ...form.values,
      embedVideo: '',
      mediaSource: '',
      remoteMediaUrl: '',
      imageCredit: '',
    });
  };

  const handleMediaChange = ({ media }) => {
    removeMedia();

    if (media.videoIframe) {
      form.setFieldValue('embedVideo', media.videoIframe);
    } else {
      form.setValues({
        ...form.values,
        mediaSource: media.source,
        remoteMediaUrl: media.url,
        imageCredit: media.photographerUrl,
      });
    }
  };

  const handlePoints = points => form.setFieldValue('points', points);

  return {
    ...form,
    admins,
    handleShowDisclaimer,
    addAttachment,
    removeAttachment,
    handleAddAnswer,
    handleRemoveAnswer,
    handleQuestionSubTypeOnChange,
    handleQuestionOnChange,
    handleMediaChange,
    handlePoints,
    removeMedia,
    setInitialValues,
    saveAndNavigate,
    autoSaved,
  };
};
